import { useCallback, useEffect, useMemo, useState } from "react";

const LS_PREFIX = "freight_admin_table";

function readPrefs(tableId) {
  try {
    const raw = localStorage.getItem(`${LS_PREFIX}:${tableId}`);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

function writePrefs(tableId, prefs) {
  try {
    localStorage.setItem(`${LS_PREFIX}:${tableId}`, JSON.stringify(prefs));
  } catch {
    /* ignore */
  }
}

/** Merge patch into existing prefs so visibility + column order stay in sync. */
function writePrefsMerged(tableId, patch) {
  const prev = readPrefs(tableId);
  writePrefs(tableId, { ...prev, ...patch });
}

function buildVisibilityFromStorage(tableId, columnDefs) {
  const saved = readPrefs(tableId);
  const vis = {};
  columnDefs.forEach((d) => {
    if (saved.columns && Object.prototype.hasOwnProperty.call(saved.columns, d.key)) {
      vis[d.key] = !!saved.columns[d.key];
    } else {
      vis[d.key] = d.defaultVisible !== false;
    }
  });
  return vis;
}

/** Merge saved key order with current column defs (append new keys, drop removed). */
export function mergeColumnOrderKeys(columnDefs, savedOrder) {
  const keys = columnDefs.map((d) => d.key);
  if (!Array.isArray(savedOrder) || savedOrder.length === 0) return keys;
  const seen = new Set();
  const out = [];
  for (const k of savedOrder) {
    if (keys.includes(k) && !seen.has(k)) {
      out.push(k);
      seen.add(k);
    }
  }
  for (const k of keys) {
    if (!seen.has(k)) out.push(k);
  }
  return out;
}

export function orderColumnDefsByKeys(columnDefs, orderKeys) {
  const map = new Map(columnDefs.map((d) => [d.key, d]));
  return orderKeys.map((k) => map.get(k)).filter(Boolean);
}

/**
 * Column visibility + column order for freight admin tables — **localStorage only** (no server calls).
 * Pass `orderedColumnDefs` and `onColumnReorder` into {@link ../components/datatable/AdminDataTable.jsx} /
 * {@link ../components/datatable/AdminServerList.jsx} for persisted drag-and-drop order.
 */
export function useFreightAdminTablePrefs(tableId, columnDefs) {
  const columnKeysSig = useMemo(() => columnDefs.map((d) => d.key).join("\0"), [columnDefs]);

  const defaultVisibility = useMemo(() => {
    const m = {};
    columnDefs.forEach((d) => {
      m[d.key] = d.defaultVisible !== false;
    });
    return m;
  }, [columnDefs]);

  const defaultOrderKeys = useMemo(() => columnDefs.map((d) => d.key), [columnDefs]);

  const [visibility, setVisibility] = useState(() => buildVisibilityFromStorage(tableId, columnDefs));

  const [columnOrderKeys, setColumnOrderKeys] = useState(() =>
    mergeColumnOrderKeys(columnDefs, readPrefs(tableId).columnOrder)
  );

  useEffect(() => {
    const saved = readPrefs(tableId);
    const nextVis = buildVisibilityFromStorage(tableId, columnDefs);
    setVisibility((prev) => (JSON.stringify(prev) === JSON.stringify(nextVis) ? prev : nextVis));
    const nextOrder = mergeColumnOrderKeys(columnDefs, saved.columnOrder);
    setColumnOrderKeys((prev) => (JSON.stringify(prev) === JSON.stringify(nextOrder) ? prev : nextOrder));
  }, [tableId, columnKeysSig, columnDefs]);

  const persistColumns = useCallback((next) => {
    writePrefsMerged(tableId, { columns: next });
  }, [tableId]);

  const toggleColumn = useCallback(
    (key) => {
      setVisibility((v) => {
        const next = { ...v, [key]: !v[key] };
        persistColumns(next);
        return next;
      });
    },
    [persistColumns]
  );

  const resetColumnsVisible = useCallback(() => {
    setVisibility(() => {
      const next = { ...defaultVisibility };
      writePrefsMerged(tableId, { columns: next });
      return next;
    });
  }, [defaultVisibility, tableId]);

  const resetColumnOrder = useCallback(() => {
    setColumnOrderKeys(defaultOrderKeys);
    writePrefsMerged(tableId, { columnOrder: defaultOrderKeys });
  }, [defaultOrderKeys, tableId]);

  const onColumnReorder = useCallback(
    (dragKey, dropKey) => {
      if (!dragKey || dragKey === dropKey) return;
      setColumnOrderKeys((keys) => {
        const without = keys.filter((k) => k !== dragKey);
        const insertAt = without.indexOf(dropKey);
        if (insertAt < 0) return keys;
        const next = [...without.slice(0, insertAt), dragKey, ...without.slice(insertAt)];
        writePrefsMerged(tableId, { columnOrder: next });
        return next;
      });
    },
    [tableId]
  );

  const orderedColumnDefs = useMemo(
    () => orderColumnDefsByKeys(columnDefs, columnOrderKeys),
    [columnDefs, columnOrderKeys]
  );

  return {
    visibility,
    toggleColumn,
    resetColumnsVisible,
    resetColumnOrder,
    orderedColumnDefs,
    onColumnReorder,
    defaultVisibility
  };
}
